import ApiService from '@services/api.service'
import { Notify } from 'quasar'

//Helper destinée à créer la structure initiale des données.
const createInitialDataStructure = () => ({
  domainesCompetences: '', // Champ de texte simple
  besoinsAttentesFreins: { selected: [], freeText: '' },
  domainesCompetencesParcours: { selected: [], freeText: '' }
})

export const suiviRpp = {
  namespaced: true,
  state: {
    //Listes et collections.
    suivi_rpps: {},
    type_rpps: [],
    competences_rpp: [],
    competences_by_domaine: {},
    resource_data: [], //Stocke les données pour la modale
    loading_resources: false,

    //Données sélectionnées.
    selected_suivi_rpp: null,

    //États UI.
    loading_suivi_rpps: false,
    loading_type_rpps: false,
    loading_competences: false,
    loading_create: false,
    loading_update: false,
    loading_delete: false,
    loading_upload: false,

    //Formulaire
    form_data: createInitialDataStructure(),

    //Modales
    show_competences_modal: false,

    //Compétences sélectionnées temporairement.
    current_field: null
  },

  mutations: {
    //Gestion des suivis RPP.
    SET_SUIVI_RPPS(state, { bilan_id, suivis }) {
      state.suivi_rpps[bilan_id] = suivis
    },
    ADD_SUIVI_RPP(state, { bilan_id, suivi }) {
      if (!state.suivi_rpps[bilan_id]) {
        state.suivi_rpps[bilan_id] = []
      }
      state.suivi_rpps[bilan_id].push(suivi)
    },
    UPDATE_SUIVI_RPP(state, { bilan_id, suivi }) {
      if (!state.suivi_rpps[bilan_id]) return
      const index = state.suivi_rpps[bilan_id].findIndex(
        (s) => s.id === suivi.id
      )
      if (index !== -1) {
        state.suivi_rpps[bilan_id].splice(index, 1, suivi)
      }
    },
    DELETE_SUIVI_RPP(state, { bilan_id, suivi_id }) {
      if (!state.suivi_rpps[bilan_id]) return
      state.suivi_rpps[bilan_id] = state.suivi_rpps[bilan_id].filter(
        (s) => s.id !== suivi_id
      )
    },
    SET_SELECTED_SUIVI_RPP(state, suivi) {
      state.selected_suivi_rpp = suivi
    },

    //Gestion des types RPP.
    SET_TYPE_RPPS(state, types) {
      state.type_rpps = types
    },

    //Gestion des compétences.
    SET_COMPETENCES_RPP(state, competences) {
      state.competences_rpp = competences
    },
    SET_COMPETENCES_BY_DOMAINE(state, competences_by_domaine) {
      state.competences_by_domaine = competences_by_domaine
    },

    //Gestion du formulaire.
    SET_FORM_DATA(state, data) {
      state.form_data = { ...state.form_data, ...data }
    },
    RESET_FORM_DATA(state) {
      state.form_data = createInitialDataStructure()
    },
    SET_LOADING_RESOURCES(state, status) {
      state.loading_resources = status
    },
    SET_RESOURCE_DATA(state, data) {
      state.resource_data = data
    },

    //Gestion des modales.
    SET_COMPETENCES_MODAL(state, show) {
      state.show_competences_modal = show
    },
    SET_CURRENT_FIELD(state, field) {
      state.current_field = field
    },

    //Gestion des états de chargement.
    SET_LOADING_SUIVI_RPPS(state, status) {
      state.loading_suivi_rpps = status
    },
    SET_LOADING_TYPE_RPPS(state, status) {
      state.loading_type_rpps = status
    },
    SET_LOADING_COMPETENCES(state, status) {
      state.loading_competences = status
    },
    SET_LOADING_CREATE(state, status) {
      state.loading_create = status
    },
    SET_LOADING_UPDATE(state, status) {
      state.loading_update = status
    },
    SET_LOADING_DELETE(state, status) {
      state.loading_delete = status
    },
    SET_LOADING_UPLOAD(state, status) {
      state.loading_upload = status
    }
  },

  actions: {
    /**
     * Charge les types de RPP disponibles.
     */
    async fetchTypeRpps({ commit }) {
      commit('SET_LOADING_TYPE_RPPS', true)
      try {
        const response = await ApiService.getRequest('type_rpps')
        commit('SET_TYPE_RPPS', response.data['hydra:member'])
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: 'Erreur lors du chargement des types RPP'
        })
      } finally {
        commit('SET_LOADING_TYPE_RPPS', false)
      }
    },

    /**
     * Charge les ressources pour la modale de sélection.
     * S'adapte en fonction du type de ressource demandé.
     */
    async fetchResources({ commit }, { resourceType, studentId }) {
      commit('SET_LOADING_RESOURCES', true)
      commit('SET_RESOURCE_DATA', [])
      let endpoint = ''

      try {
        switch (resourceType) {
          case 'METIER':
            if (!studentId)
              throw new Error(
                "L'ID de le bénéficiaire est requis pour les compétences métier."
              )
            endpoint = `etudiants/${studentId}/competences_metiers_rpp`
            break
          case 'CSP_SEP_EP':
            endpoint = 'referentiel_competences?type=CSP_SEP_EP'
            break
          case 'DOMAINE_1_10':
            endpoint = 'referentiel_competences?type=DOMAINE_1_10'
            break
          default:
            throw new Error(`Type de ressource inconnu: ${resourceType}`)
        }

        const response = await ApiService.getRequest(endpoint)
        const data = response.data['hydra:member'] || response.data
        commit('SET_RESOURCE_DATA', data)
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: `Erreur lors du chargement des ressources (${resourceType})`
        })
        console.error(error)
      } finally {
        commit('SET_LOADING_RESOURCES', false)
      }
    },

    /**
     * Charge les suivis RPP d'un bilan.
     */
    async fetchSuiviRpps({ commit }, bilan_id) {
      commit('SET_LOADING_SUIVI_RPPS', true)
      try {
        const response = await ApiService.getRequest(
          `suivi_rpps?etudiantBilanParcours=${bilan_id}`
        )
        commit('SET_SUIVI_RPPS', {
          bilan_id,
          suivis: response.data['hydra:member']
        })
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: 'Erreur lors du chargement des suivis RPP'
        })
      } finally {
        commit('SET_LOADING_SUIVI_RPPS', false)
      }
    },

    /**
     * Crée ou met à jour un suivi RPP.
     */
    async saveSuiviRpp(
      { commit, dispatch },
      { assessment, type_rpp, data, suivi_id = null }
    ) {
      const isLoadingMutation = suivi_id
        ? 'SET_LOADING_UPDATE'
        : 'SET_LOADING_CREATE'
      commit(isLoadingMutation, true)

      try {
        //Transformation des données du formulaire pour l'API
        const selectedDataForApi = {}
        for (const key in data) {
          if (data[key] && typeof data[key] === 'object') {
            selectedDataForApi[key] = {
              //Extraire les IDs des objets compétences sélectionnées
              selected_ids: data[key].selected
                ? data[key].selected.map((comp) => comp.id)
                : [],
              freeText: data[key].freeText || ''
            }
          } else {
            //Gérer les champs simples comme le CV (si applicable)
            selectedDataForApi[key] = data[key]
          }
        }

        const payload = {
          selectedData: selectedDataForApi,
          status: 1 //Statut "En cours" par défaut
        }

        let response
        if (suivi_id) {
          // Mise à jour d'un suivi existant
          response = await ApiService.putRequest(
            `suivi_rpps/${suivi_id}`,
            payload
          )
          Notify.create({
            type: 'positive',
            message: 'Suivi RPP mis à jour avec succès'
          })
        } else {
          // Création d'un nouveau suivi
          payload.etudiantBilanParcours = assessment['@id']
          payload.typeRpp = type_rpp['@id']
          response = await ApiService.postRequest('suivi_rpps', payload)
          Notify.create({
            type: 'positive',
            message: 'Suivi RPP créé avec succès'
          })
        }

        //Rafraîchir la liste des suivis pour le bilan courant.
        await dispatch('fetchSuiviRpps', assessment.id)

        return response.data
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: 'Erreur lors de la sauvegarde du suivi RPP'
        })
        console.error(error)
        throw error
      } finally {
        commit(isLoadingMutation, false)
      }
    },

    /**
     * Supprime un suivi RPP.
     */
    async deleteSuiviRpp({ commit, dispatch }, { bilan_id, suivi_id }) {
      commit('SET_LOADING_DELETE', true)
      try {
        await ApiService.deleteRequest(`suivi_rpps/${suivi_id}`)
        commit('DELETE_SUIVI_RPP', { bilan_id, suivi_id })

        //Actualiser les données du bilan pour refléter la suppression.
        try {
          const response = await ApiService.getRequest(
            `etudiant_bilan_parcours/${bilan_id}`
          )
          await dispatch(
            'bilanParcours/updateAssessmentInStore',
            {
              assessment_data: response.data
            },
            { root: true }
          )
        } catch (syncError) {
          console.warn('Erreur lors de la synchronisation du bilan:', syncError)
        }

        Notify.create({
          type: 'positive',
          message: 'Suivi RPP supprimé avec succès'
        })
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: 'Erreur lors de la suppression du suivi RPP'
        })
        throw error
      } finally {
        commit('SET_LOADING_DELETE', false)
      }
    },

    /**
     * Upload un document pour un suivi RPP.
     */
    async uploadDocument(
      { commit },
      { suivi_id, file, nom, type_document = 'cv' }
    ) {
      commit('SET_LOADING_UPLOAD', true)
      try {
        const formData = new FormData()
        formData.append('suiviRpp', `suivi_rpps/${suivi_id}`)
        formData.append('nom', nom)
        formData.append('typeDocument', type_document)
        formData.append('file', file)

        const response = await ApiService.postRequest(
          'document_suivi_rpps',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        Notify.create({
          type: 'positive',
          message: 'Document uploadé avec succès'
        })

        return response.data
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: "Erreur lors de l'upload du document"
        })
        throw error
      } finally {
        commit('SET_LOADING_UPLOAD', false)
      }
    },

    /**
     * Supprime un document d'un suivi RPP.
     */
    async deleteDocument(
      { commit, state },
      { bilan_id, suivi_id, document_id }
    ) {
      commit('SET_LOADING_UPLOAD', true) // Reuse upload loading state
      try {
        await ApiService.deleteRequest(`document_suivi_rpps/${document_id}`)

        const suiviToUpdate = state.suivi_rpps[bilan_id]?.find(
          (s) => s.id === suivi_id
        )
        if (suiviToUpdate) {
          const updatedSuivi = {
            ...suiviToUpdate,
            documents: suiviToUpdate.documents.filter(
              (d) => d.uuid !== document_id
            )
          }
          commit('UPDATE_SUIVI_RPP', { bilan_id, suivi: updatedSuivi })
        }

        Notify.create({
          type: 'positive',
          message: 'Document supprimé avec succès'
        })
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: 'Erreur lors de la suppression du document'
        })
        throw error
      } finally {
        commit('SET_LOADING_UPLOAD', false)
      }
    },

    openCompetencesModal({ commit }, field) {
      commit('SET_CURRENT_FIELD', field)
      commit('SET_COMPETENCES_MODAL', true)
    },

    closeCompetencesModal({ commit }) {
      commit('SET_COMPETENCES_MODAL', false)
      commit('SET_CURRENT_FIELD', null)
    },

    addSelectedCompetencesToField({ commit, state }, { competences }) {
      const field = state.current_field
      if (!field || !state.form_data[field]) return

      const existingCompetences = state.form_data[field].selected || []
      const updatedCompetences = [...existingCompetences]

      competences.forEach((newComp) => {
        if (
          !existingCompetences.some((existing) => existing.id === newComp.id)
        ) {
          updatedCompetences.push(newComp)
        }
      })

      commit('SET_FORM_DATA', {
        ...state.form_data,
        [field]: {
          ...state.form_data[field],
          selected: updatedCompetences
        }
      })

      commit('SET_COMPETENCES_MODAL', false)
      commit('SET_CURRENT_FIELD', null)
    }
  },

  getters: {
    getSuiviRppsByBilan: (state) => (bilan_id) => {
      return state.suivi_rpps[bilan_id] || []
    },
    getActiveTypeRpps: (state) => {
      return state.type_rpps.filter((type) => type.actif)
    },
    getCompetencesGroupedByDomaine: (state) => {
      return state.competences_by_domaine
    },
    isLoading: (state) => {
      return (
        state.loading_suivi_rpps ||
        state.loading_type_rpps ||
        state.loading_competences ||
        state.loading_create ||
        state.loading_update ||
        state.loading_delete ||
        state.loading_upload
      )
    },
    getResourceData: (state) => state.resource_data,
    isLoadingResources: (state) => state.loading_resources
  }
}

export default suiviRpp
